import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { AlertTriangle, Activity, RefreshCw, TrendingUp, Loader2, Eye } from 'lucide-react';
import api from '../utils/api';

const STATUS_COLOR = (status) => {
  if (status >= 500) return 'bg-rose-100 text-rose-700 border-rose-200';
  if (status === 404) return 'bg-amber-100 text-amber-700 border-amber-200';
  if (status === 401 || status === 403) return 'bg-purple-100 text-purple-700 border-purple-200';
  if (status >= 400) return 'bg-orange-100 text-orange-700 border-orange-200';
  return 'bg-slate-100 text-slate-600 border-slate-200';
};

const METHOD_COLOR = {
  GET: 'text-blue-600',
  POST: 'text-emerald-600',
  PUT: 'text-amber-600',
  PATCH: 'text-purple-600',
  DELETE: 'text-rose-600',
};

const fmt = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

export default function SystemLogsPage({ user, onLogout }) {
  const [tab, setTab] = useState('errors');
  const [days, setDays] = useState(7);
  const [errors, setErrors] = useState({ total: 0, items: [], top_failing: [] });
  const [activity, setActivity] = useState({ total: 0, items: [] });
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const loadErrors = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/system-logs/errors?days=${days}&limit=300`);
      setErrors(res.data);
    } catch (err) {
      toast.error('Could not load error log');
    } finally { setLoading(false); }
  }, [days]);

  const loadActivity = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/system-logs/activity?days=${days}&limit=300`);
      setActivity(res.data);
    } catch (err) {
      toast.error('Could not load activity log');
    } finally { setLoading(false); }
  }, [days]);

  useEffect(() => {
    if (tab === 'errors') loadErrors(); else loadActivity();
  }, [tab, loadErrors, loadActivity]);

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        <header className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Activity className="w-6 h-6 text-indigo-600" /> System Logs
            </h1>
            <p className="text-slate-500 text-sm mt-1">Track failed actions and recent activity across the clinic app.</p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={String(days)} onValueChange={(v) => setDays(parseInt(v, 10))}>
              <SelectTrigger className="rounded-xl w-36" data-testid="system-logs-range"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Last 24 hours</SelectItem>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="14">Last 14 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" className="rounded-xl" onClick={() => (tab === 'errors' ? loadErrors() : loadActivity())} data-testid="system-logs-refresh">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
        </header>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="rounded-xl">
            <TabsTrigger value="errors" className="rounded-lg" data-testid="system-logs-tab-errors">
              <AlertTriangle className="w-4 h-4 mr-2" /> Errors
              <Badge className="ml-2 bg-rose-100 text-rose-700 border-rose-200 text-xs">{errors.total}</Badge>
            </TabsTrigger>
            <TabsTrigger value="activity" className="rounded-lg" data-testid="system-logs-tab-activity">
              <Activity className="w-4 h-4 mr-2" /> Activity
              <Badge className="ml-2 bg-emerald-100 text-emerald-700 border-emerald-200 text-xs">{activity.total}</Badge>
            </TabsTrigger>
          </TabsList>

          {/* Errors tab */}
          <TabsContent value="errors" className="space-y-4">
            {/* Top-failing endpoints */}
            {errors.top_failing && errors.top_failing.length > 0 && (
              <Card className="p-4 rounded-2xl border-slate-200">
                <h3 className="font-semibold text-slate-900 flex items-center gap-2 mb-3">
                  <TrendingUp className="w-4 h-4 text-rose-600" /> Most failing endpoints
                </h3>
                <div className="flex flex-wrap gap-2">
                  {errors.top_failing.map((t, i) => (
                    <Badge key={i} className="border bg-white text-slate-800 border-slate-200 text-xs font-normal" data-testid={`error-top-${i}`}>
                      <span className={`mr-1.5 ${STATUS_COLOR(t.status)} px-1.5 py-0.5 rounded`}>{t.status}</span>
                      <code className="text-[11px]">{t.path}</code>
                      <span className="ml-2 text-slate-500">× {t.count}</span>
                    </Badge>
                  ))}
                </div>
              </Card>
            )}

            <Card className="rounded-2xl border-slate-200 overflow-hidden">
              <div className="p-3 border-b border-slate-100 flex items-center justify-between bg-slate-50">
                <span className="text-sm font-medium text-slate-700">{errors.total} errors in the last {days} day{days === 1 ? '' : 's'}</span>
              </div>
              {loading && errors.items.length === 0 ? (
                <div className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
              ) : errors.items.length === 0 ? (
                <div className="py-10 text-center text-slate-400">No errors in this period 🎉</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
                    <tr>
                      <th className="px-4 py-3 text-left">Time</th>
                      <th className="px-4 py-3 text-left">Status</th>
                      <th className="px-4 py-3 text-left">Method</th>
                      <th className="px-4 py-3 text-left">Endpoint</th>
                      <th className="px-4 py-3 text-left">User</th>
                      <th className="px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {errors.items.map((e) => (
                      <React.Fragment key={e.id}>
                        <tr className="hover:bg-slate-50" data-testid={`error-row-${e.id}`}>
                          <td className="px-4 py-2.5 text-slate-600 whitespace-nowrap">{fmt(e.timestamp)}</td>
                          <td className="px-4 py-2.5"><Badge className={`border text-xs ${STATUS_COLOR(e.status_code)}`}>{e.status_code}</Badge></td>
                          <td className={`px-4 py-2.5 font-mono text-xs font-semibold ${METHOD_COLOR[e.method] || 'text-slate-700'}`}>{e.method}</td>
                          <td className="px-4 py-2.5 font-mono text-xs text-slate-700">{e.path}</td>
                          <td className="px-4 py-2.5 text-slate-600 text-xs">{e.user_name || e.user_id || '—'}</td>
                          <td className="px-4 py-2.5 text-right">
                            <Button size="sm" variant="ghost" onClick={() => setExpanded(expanded === e.id ? null : e.id)} data-testid={`error-detail-${e.id}`}>
                              <Eye className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                        {expanded === e.id && (
                          <tr className="bg-slate-50">
                            <td colSpan={6} className="px-6 py-3 text-xs text-slate-700 space-y-1">
                              {e.query && <div><strong>Query:</strong> <code>{e.query}</code></div>}
                              {e.exc_type && <div><strong>Exception:</strong> {e.exc_type}</div>}
                              <div><strong>Detail:</strong> <code>{e.detail}</code></div>
                              {e.client_ip && <div><strong>Client IP:</strong> {e.client_ip}</div>}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              )}
            </Card>
            <p className="text-xs text-slate-400">Errors auto-purge after 30 days.</p>
          </TabsContent>

          {/* Activity tab */}
          <TabsContent value="activity">
            <Card className="rounded-2xl border-slate-200 overflow-hidden">
              <div className="p-3 border-b border-slate-100 flex items-center justify-between bg-slate-50">
                <span className="text-sm font-medium text-slate-700">{activity.total} actions in the last {days} day{days === 1 ? '' : 's'}</span>
              </div>
              {loading && activity.items.length === 0 ? (
                <div className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
              ) : activity.items.length === 0 ? (
                <div className="py-10 text-center text-slate-400">No activity in this period.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
                    <tr>
                      <th className="px-4 py-3 text-left">Time</th>
                      <th className="px-4 py-3 text-left">User</th>
                      <th className="px-4 py-3 text-left">Role</th>
                      <th className="px-4 py-3 text-left">Action</th>
                      <th className="px-4 py-3 text-left">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {activity.items.map((a) => (
                      <tr key={a.id} className="hover:bg-slate-50" data-testid={`activity-row-${a.id}`}>
                        <td className="px-4 py-2.5 text-slate-600 whitespace-nowrap">{fmt(a.timestamp)}</td>
                        <td className="px-4 py-2.5 font-medium text-slate-900">{a.user_name}</td>
                        <td className="px-4 py-2.5"><Badge variant="outline" className="text-xs">{a.user_role}</Badge></td>
                        <td className="px-4 py-2.5"><code className="text-xs text-indigo-600">{a.action_type}</code></td>
                        <td className="px-4 py-2.5 text-slate-700">{a.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
